import { createApi } from '@reduxjs/toolkit/query/react';
import { authBaseQuery } from '../../app/authBaseQuery';
import { parseLaravelPaginator } from '../../lib/parseLaravelPaginator';
import { unwrapMaybeWrapped } from '../../lib/unwrapResource';
import { normalizeKycDocument, normalizeOtherDocumentItem, normalizeKycProgress } from '../kyc/kycApi';

/**
 * Real-backend Investors browsing surface (v1.12.0) — GET /admin/investors
 * (list) and GET /admin/investors/{uuid} (detail), gated by manage-
 * investors. Scoped server-side to role: investor only. Both GET endpoints
 * return their resource directly (bare paginator for the list, a bare
 * AdminInvestorView for the detail) — no {status,data} wrapper the way KYC
 * and Investment Plans use, so this doesn't reuse either of those parsers.
 *
 * Distinct from the old mock-backed features/investors/investorsApi.js
 * (still present, still unused by anything — same "superseded, not
 * deleted" treatment as plansApi.js after investmentPlansApi.js replaced
 * it), which targeted made-up endpoints like /admin/investors/manual and
 * activate/deactivate that don't exist in this spec.
 */
function normalizeAdminInvestorView(raw) {
  if (!raw) return null;
  const firstName = raw.first_name ?? '';
  const lastName = raw.last_name ?? '';
  return {
    id: raw.uuid ?? raw.id,
    first_name: firstName,
    last_name: lastName,
    full_name: [firstName, lastName].filter(Boolean).join(' ') || raw.email,
    email: raw.email ?? null,
    phone: raw.phone ?? null,
    roles: raw.roles ?? raw.role ?? [],
    must_change_password: !!raw.must_change_password,
    account_status: raw.account_status ?? null,
    suspended_at: raw.suspended_at ?? null,
    last_login_at: raw.last_login_at ?? null,
    created_at: raw.created_at ?? null,
    updated_at: raw.updated_at ?? null,
    // v1.18.0 — this investor's identity submission documents plus any
    // "other" documents attached (self-uploaded or admin-attached), and the
    // same completion-percent breakdown the investor's own /investor/kyc
    // screen shows. Reuses kycApi.js's normalizers so the math/shape can't
    // drift between the two screens — see that file for the 50/50 rule.
    documents: (raw.documents ?? []).map(normalizeKycDocument),
    kyc_progress: raw.kyc_progress
      ? normalizeKycProgress(raw.kyc_progress, {
          identityApproved: raw.account_status === 'active',
          otherItems: (raw.other_documents ?? []).map(normalizeOtherDocumentItem),
        })
      : null,
  };
}

export const adminInvestorsApi = createApi({
  reducerPath: 'adminInvestorsApi',
  baseQuery: authBaseQuery(),
  tagTypes: ['AdminInvestor'],
  endpoints: (builder) => ({
    // GET /admin/investors?status=&search=&per_page=&page=
    listAdminInvestors: builder.query({
      query: ({ status, search, page, per_page } = {}) => ({
        url: '/admin/investors',
        method: 'GET',
        params: { status, search, page, per_page },
      }),
      transformResponse: (response) => {
        const { items, meta, parseError } = parseLaravelPaginator(response, { context: 'adminInvestorsApi:list' });
        return { investors: items.map(normalizeAdminInvestorView), meta, parseError: !!parseError };
      },
      providesTags: (result) => [
        ...(result?.investors ?? []).map(({ id }) => ({ type: 'AdminInvestor', id })),
        { type: 'AdminInvestor', id: 'LIST' },
      ],
    }),

    // GET /admin/investors/{uuid} — 404 (not 403) for a uuid that exists
    // but isn't an investor, same enumeration-safety pattern as
    // GET /admin/admins/{uuid} in reverse. Don't try to tell "doesn't
    // exist" apart from "not an investor" — authErrorMessage's `notFound`
    // override covers both with one message.
    getAdminInvestor: builder.query({
      query: (uuid) => ({ url: `/admin/investors/${uuid}`, method: 'GET' }),
      transformResponse: (response) => normalizeAdminInvestorView(unwrapMaybeWrapped(response)),
      providesTags: (result, error, uuid) => [{ type: 'AdminInvestor', id: uuid }],
    }),

    // POST /admin/investors/{uuid}/documents — v1.18.0 "Attach Document" on
    // behalf of an investor (multipart: tag + file). Gated by
    // manage-investors, NOT manage-kyc-documents — this is an investor-
    // record action (same permission that gates this whole screen and
    // "Create investment"), independent of the separate template-management
    // gate. Endpoint path/shape is this file's best-effort construction —
    // see kycTemplatesApi.js's header for why (network unreachable this
    // session) and the same "confirm before shipping" caveat applies here.
    adminAttachInvestorDocument: builder.mutation({
      query: ({ uuid, formData }) => ({ url: `/admin/investors/${uuid}/documents`, method: 'POST', data: formData }),
      invalidatesTags: (result, error, { uuid }) => [{ type: 'AdminInvestor', id: uuid }],
    }),
  }),
});

export const { useListAdminInvestorsQuery, useGetAdminInvestorQuery, useAdminAttachInvestorDocumentMutation } =
  adminInvestorsApi;
